import { useState, useContext } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Navbar from "../components/Navbar";
import { FaCheckCircle } from "react-icons/fa";
import { FaLocationArrow, FaPhone } from "react-icons/fa6";
import { MdEmail } from "react-icons/md";
import TenantContext from "../Context/TenantContext.jsx";

const Contact = () => {
  const { tenant = {} } = useContext(TenantContext) || {};

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Send email using the backend API
      const response = await axios.post(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/email/contact`,
        formData
      );
      
      toast.success(response.data.message || "Your message has been sent. We'll get back to you soon!");
      
      // Show success state
      setIsSubmitted(true);
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      console.error("Error sending contact form:", error);
      toast.error(error.response?.data?.message || "Failed to send your message. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewMessage = () => {
    setIsSubmitted(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main>
        {/* Hero Section */}
        <div className="bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8 flex flex-col items-center">
            <h1 className="text-4xl font-bold mb-4 text-center">Contact Us</h1>
            <p className="text-xl max-w-3xl text-center">
              Have questions or need assistance? Our team is here to help you.
            </p>
          </div>
        </div>

        {/* Contact Info & Form Section */}
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Get in Touch</h2>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-100 text-blue-600">
                      <FaLocationArrow className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Our Location</h3>
                    <p className="mt-1 text-gray-600">
                      <a 
                        href={tenant.googleMapsLink || `https://maps.google.com/maps?q=${encodeURIComponent(tenant.address || 'PO Box 2011 Dublin, CA 94568')}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-blue-600 underline"
                      >
                        {tenant.address || 'PO Box 2011 Dublin, CA 94568'}
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-100 text-blue-600">
                      <FaPhone className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Phone Number</h3>
                    <p className="mt-1 text-gray-600">
                      <a href={`tel:${(tenant.contactPhone || '').replace(/[^+\d]/g,'')}`} className="hover:text-blue-600">{tenant.contactPhone || '+(1) 925 336 1097'}</a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-100 text-blue-600">
                      <MdEmail className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Email</h3>
                    <p className="mt-1 text-gray-600">
                      <a href={`mailto:${tenant.contactEmail || 'healthsnai@gmail.com'}`} className="hover:text-blue-600">{tenant.contactEmail || 'healthsnai@gmail.com'}</a>
                    </p>
                  </div>
                </div>
              </div>
              
            </div>

            {/* Contact Form or Success Message */}
            <div className="bg-white rounded-lg shadow-md p-8">
              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center h-full py-8">
                  <div className="text-green-500 mb-4">
                    <FaCheckCircle className="h-16 w-16" />
                  </div>
                  <h2 className="text-2xl font-bold mb-3 text-gray-800 text-center">Message Sent Successfully!</h2>
                  <p className="text-gray-600 mb-8 text-center">
                    Thank you for reaching out. Our team will review your message and get back to you soon.
                  </p>
                  <button
                    onClick={handleNewMessage}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold mb-6 text-gray-800">Send Us a Message</h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your name"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700">Subject</label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="How can we help you?"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="4"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your message here..."
                      ></textarea>
                    </div>

                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Sending...
                          </>
                        ) : 'Send Message'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
            <div className="bg-blue-600 text-white px-6 py-4">
              <h2 className="text-2xl font-bold">Find Us</h2>
              <p className="text-blue-100 mt-1">Visit our location or get directions</p>
            </div>
            <div className="relative">
              <iframe 
                src={tenant.maps?.embedUrl || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3156.9389791965054!2d-121.935792!3d37.715540!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808ff3b62d970e13%3A0x1ff86a7d3e55c63!2sPO%20Box%202011%2C%20Dublin%2C%20CA%2094568!5e0!3m2!1sen!2sus!4v1697043240000!5m2!1sen!2sus"} 
                width="100%" 
                height="500" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title={`${tenant.name || 'Smart Nurse'} Location`}
                className="w-full"
              ></iframe>
              <div className="absolute bottom-4 left-4 bg-white bg-opacity-90 rounded-lg p-3 shadow-md">
                <p className="text-sm font-medium text-gray-800">
                  {tenant.address || 'PO Box 2011 Dublin, CA 94568'}
                </p>
                <a 
                  href={tenant.googleMapsLink || `https://maps.google.com/maps?q=${encodeURIComponent(tenant.address || 'PO Box 2011 Dublin, CA 94568')}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium underline mt-1 inline-block"
                >
                  Get Directions →
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Contact;
